import React, { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import styled from '@emotion/styled';
import { colors, mediaQuery } from '@modernhealth/vital-foundation';
import { HeadingText60, BodyTextSmall, pxToRem } from '@modernhealth/vital-components';
import dailyPauseStatTileStars from 'src/public/img/daily-pause-stat-tile-stars.svg';
import dailyPauseStatTileBalloon from 'src/public/img/daily-pause-stat-tile-balloon.svg';
import { DailyPauseTileTestID } from './testIds';

const Wrapper = styled.div({
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    maxWidth: pxToRem(432),

    [mediaQuery.minWidth('sm')]: {
        flexDirection: 'row',
    },
});

const TileWrapper = styled.div({
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    border: `2px solid ${colors.gray05}`,
    borderRadius: '7px',
    minWidth: '211px',
    overflow: 'hidden',
    marginBottom: 12,

    [mediaQuery.minWidth('sm')]: {
        display: 'flex',
        flexDirection: 'row',
        marginBottom: 12,
    },
});

const TileStats = styled.div({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    marginLeft: '16px',
});

const ImageContainer = styled.div({
    position: 'relative',
});

const Image = styled.img({
    position: 'absolute',
    bottom: 0,
    right: 0,
    opacity: 0.5,
});

interface DailyPauseTileProps {
    number?: number;
    description: string;
    image: ReactNode;
}

export const DailyPauseTile: React.FC<DailyPauseTileProps> = ({ number, description, image }) => {
    return (
        <TileWrapper data-testid={DailyPauseTileTestID.Tile}>
            <TileStats data-testid={DailyPauseTileTestID.Stats}>
                <HeadingText60 marginTop={16} marginBottom={0}>
                    {number}
                </HeadingText60>{' '}
                <BodyTextSmall marginTop={0} marginBottom={8} fontColor="gray65">
                    {description}
                </BodyTextSmall>
            </TileStats>
            <ImageContainer>{image}</ImageContainer>
        </TileWrapper>
    );
};

interface DailyPauseStatsTilesProps {
    completedCount?: number;
    weekStreak?: number;
}

export const DailyPauseStatsTiles: React.FC<DailyPauseStatsTilesProps> = ({
    completedCount,
    weekStreak,
}) => {
    const { t } = useTranslation();

    const description =
        completedCount === 1
            ? t('dailyPause:streaks.tile.daily_pause')
            : t('dailyPause:streaks.tile.daily_pause_plural');

    return (
        <Wrapper>
            <DailyPauseTile
                number={completedCount}
                description={description}
                image={<Image src={dailyPauseStatTileStars} alt="" />}
            />
            <DailyPauseTile
                number={weekStreak}
                description={t('dailyPause:streaks.tile.best_week_streak')}
                image={<Image src={dailyPauseStatTileBalloon} alt="" />}
            />
        </Wrapper>
    );
};
